import { Controller } from "@hotwired/stimulus"
import { pluralize } from "web/services/inflector"

export default class CounterpartyTableController extends Controller {
  static targets = [
    "table",
    "checkbox",
    "selectAll",
    "filterForm",
    "createForm",
    "popUp",
    "counter",
    "selectedContactInput",
    "dependentCheckbox",
  ]

  initialize() {
    this.selectedContactIds = new Set()
  }

  connect() {
    this.#setPopUpState()
    this.setSelectAllDisabledState()
  }

  tableTargetConnected() {
    this.#toggleDependentCheckboxes(this.selectedContactIds, true)
  }

  checkboxTargetConnected(target) {
    const targetContactIds = this.#contactIds(target)
    target.checked = this.selectedContactIds.isSupersetOf(targetContactIds)
  }

  toggleSelectAll() {
    if (this.hasDependentCheckboxTarget) {
      this.#toggleDependentCheckboxes(
        this.#allContactIdsInTable(),
        this.selectAllTarget.checked
      )
    } else {
      this.checkboxTargets.forEach((checkbox) => {
        this.#toggleSelectedContactInput(checkbox)
      })
    }

    this.#setPopUpState()
  }

  checkboxChanged(event) {
    this.#toggleSelectedContactInput(event.target)
    this.#setPopUpState()
  }

  setSelectAllDisabledState() {
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.disabled = this.checkboxTargets.every(
        (button) => button.disabled
      )
    }
  }

  submitFilterForm(turboSubmitEvent) {
    if (turboSubmitEvent.detail.success) {
      this.filterFormTarget.requestSubmit()
    }
  }

  toggleDependentCheckboxes(event) {
    this.#toggleDependentCheckboxes(
      this.#contactIds(event.target),
      event.target.checked
    )
  }

  #toggleDependentCheckboxes(contactIds, checkboxState) {
    const changedCheckboxes = []

    this.dependentCheckboxTargets.forEach((dependentCheckbox) => {
      const currentContactId = parseInt(dependentCheckbox.value)

      if (contactIds.has(currentContactId)) {
        dependentCheckbox.checked = checkboxState
        changedCheckboxes.push(dependentCheckbox)
      }
    })

    changedCheckboxes.forEach((checkbox) =>
      checkbox.dispatchEvent(new Event("change"))
    )
  }

  #setPopUpState() {
    this.popUpTarget.classList.toggle(
      "hidden",
      this.selectedContactIds.size === 0
    )

    this.counterTarget.textContent = `${this.selectedContactIds.size} ${pluralize("contact", this.selectedContactIds.size)} selected`
  }

  #toggleSelectedContactInput(checkbox) {
    this.#contactIds(checkbox).forEach((contactId) => {
      const contactAlreadySelected = this.selectedContactIds.has(contactId)

      if (checkbox.checked && !contactAlreadySelected) {
        this.#addValueToCreateForm(contactId)
      } else if (!checkbox.checked && contactAlreadySelected) {
        this.#removeValueFromCreateForm(contactId)
      }
    })
  }

  #addValueToCreateForm(contactId) {
    const hiddenInput = document.createElement("input")
    hiddenInput.type = "hidden"
    hiddenInput.name = "contact_ids[]"
    hiddenInput.value = contactId
    hiddenInput.dataset.counterpartyTableTarget = "selectedContactInput"

    this.createFormTarget.appendChild(hiddenInput)

    this.selectedContactIds.add(contactId)
  }

  #removeValueFromCreateForm(contactId) {
    const selectedContactInput = this.selectedContactInputTargets.find(
      (selectedContactInput) =>
        parseInt(selectedContactInput.value) === contactId
    )
    selectedContactInput.remove()
    this.selectedContactIds.delete(contactId)
  }

  #contactIds(element) {
    return new Set(JSON.parse(element.dataset.contactIds))
  }

  #allContactIdsInTable() {
    return this.checkboxTargets.reduce((resultingSet, checkbox) => {
      return resultingSet.union(this.#contactIds(checkbox))
    }, new Set())
  }
}
